import { useEffect, useState } from "react";
import { X } from "lucide-react";
import type { ChatContext } from "./chatStream";
import type { CompactState } from "./useAiChatSession";

export const CHAT_LENGTH_NOTICE_ID = "chat-length-notice";

const LENGTH_TOOLTIP =
  "Long chats get slower and less accurate. When the bar reaches 100% the chat limit is reached and you'll need to continue in a new chat.";

const DEFAULT_LIMIT_MESSAGE = "This chat has reached its length limit, so you can't send more messages in it.";

export type ChatLengthState = {
  context: ChatContext | null;
  limitReached: boolean;
  limitMessage: string | null;
  busyNotice: string | null;
  compactState: CompactState;
};

const FULL_PERCENT = 100;

const FULL_COLOUR = "hsl(0 72% 48%)";

const fillColour = (percent: number, isFull: boolean): string => {
  if (isFull) return FULL_COLOUR;
  const lightness = 82 - (Math.min(Math.max(percent, 0), FULL_PERCENT) / FULL_PERCENT) * 47;
  return `hsl(215 22% ${lightness.toFixed(0)}%)`;
};

export const ChatLengthBar = ({ context, limitReached }: { context: ChatContext; limitReached: boolean }) => {
  const isFull = limitReached || context.percent >= FULL_PERCENT;
  const percent = limitReached ? FULL_PERCENT : context.percent;
  return (
    <div
      title={LENGTH_TOOLTIP}
      className="flex min-w-0 max-w-md flex-1 items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs text-slate-600"
    >
      <span aria-hidden="true" className="whitespace-nowrap font-medium">
        Chat length
      </span>
      <div
        role="progressbar"
        aria-label={`Chat length: ${percent}% used`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="h-2 min-w-24 flex-1 overflow-hidden rounded-full bg-slate-100"
      >
        <div
          data-testid="chat-length-fill"
          data-full={isFull}
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.max(percent, 3)}%`, backgroundColor: fillColour(percent, isFull) }}
        />
      </div>
      <span
        aria-hidden="true"
        className={`w-8 text-right font-semibold tabular-nums ${isFull ? "text-destructive" : "text-slate-700"}`}
      >
        {percent}%
      </span>
    </div>
  );
};

export const chatLengthIndicator = (length: ChatLengthState) =>
  length.context ? <ChatLengthBar context={length.context} limitReached={length.limitReached} /> : null;

const ACTION_BUTTON =
  "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-slate-400 disabled:opacity-60";

const ChatLengthActions = ({
  compactState,
  onCompact,
  onNewChat,
}: {
  compactState: CompactState;
  onCompact: () => void;
  onNewChat: () => void;
}) => {
  const isBusy = compactState.status === "busy";
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onCompact}
        disabled={isBusy}
        className={`${ACTION_BUTTON} border-slate-700 bg-slate-700 text-white hover:bg-slate-900`}
      >
        {isBusy ? "Summarising…" : compactState.status === "failed" ? "Try again" : "Summarise and continue"}
      </button>
      <button
        type="button"
        onClick={onNewChat}
        disabled={isBusy}
        className={`${ACTION_BUTTON} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}
      >
        Start new chat
      </button>
    </div>
  );
};

const ChatLengthNotice = ({
  limitReached,
  message,
  compactState,
  onCompact,
  onNewChat,
  onDismiss,
}: {
  limitReached: boolean;
  message: string;
  compactState: CompactState;
  onCompact: () => void;
  onNewChat: () => void;
  onDismiss: () => void;
}) => (
  <div
    id={CHAT_LENGTH_NOTICE_ID}
    className={`relative mt-2 rounded-lg border px-3 py-2 text-xs text-slate-800 ${
      limitReached ? "border-destructive/50 bg-destructive/5" : "border-warning/60 bg-warning/10"
    }`}
  >
    {limitReached ? null : (
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss chat length warning"
        title="Dismiss"
        className="absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200 hover:text-slate-800 focus-visible:outline-2 focus-visible:outline-slate-400"
      >
        <X className="size-3.5" aria-hidden="true" />
      </button>
    )}
    <p className={limitReached ? undefined : "pr-7"}>{message}</p>
    {compactState.status === "failed" ? (
      <p role="alert" className="mt-1 font-medium text-destructive">
        {compactState.message}
      </p>
    ) : null}
    <ChatLengthActions compactState={compactState} onCompact={onCompact} onNewChat={onNewChat} />
  </div>
);

export const ChatLengthStatus = ({
  length,
  isSending,
  onCompact,
  onNewChat,
}: {
  length: ChatLengthState;
  isSending: boolean;
  onCompact: () => void;
  onNewChat: () => void;
}) => {
  const { context, limitReached, limitMessage, busyNotice, compactState } = length;
  const suggestCompact = context?.suggestCompact === true;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!suggestCompact) setDismissed(false);
  }, [suggestCompact]);

  const showNotice = !isSending && (limitReached || (suggestCompact && !dismissed));

  return (
    <div>
      {busyNotice ? (
        <p role="status" className="mt-2 text-xs text-slate-600">
          {busyNotice}
        </p>
      ) : null}
      {showNotice ? (
        <ChatLengthNotice
          limitReached={limitReached}
          message={
            limitReached
              ? (limitMessage ?? DEFAULT_LIMIT_MESSAGE)
              : "This chat is getting long. Summarise it and continue in a new chat for faster, more accurate answers."
          }
          compactState={compactState}
          onCompact={onCompact}
          onNewChat={onNewChat}
          onDismiss={() => setDismissed(true)}        />
      ) : null}
    </div>
  );
};
